import React, { useState, useEffect } from "react";
import "./Navbar.css";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const { t } = useLanguage();

  return (
    <>
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="navbar-container">
          {/* Logo & Brand */}
          <div className="navbar-brand">
            <a href="#home" className="brand-link" onClick={closeMenu}>
              <div className="brand-logo-wrapper">
                <img
                  src="/assets/shadi-logo.png"
                  alt="Shadi Arts"
                  className="brand-logo"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling.style.display = "flex";
                  }}
                />
                <div className="logo-fallback">🎨</div>
              </div>
              <div className="brand-text">
                <h1 className="brand-name">
                  Shadi <span className="brand-accent">Arts</span>
                </h1>
                <div className="brand-subtitle">
                  {" "}
                  {t("Portrait Artist", "فنان بورتريهات")}{" "}
                </div>
              </div>
            </a>
          </div>

          {/* Desktop Navigation */}
          <ul className={`navbar-menu ${isMenuOpen ? "active" : ""}`}>
            <li className="nav-item">
              <a href="#home" className="nav-link" onClick={closeMenu}>
                <span className="nav-icon">🏠</span>
                <span className="nav-text"> {t("Home", "الرئسية")} </span>
              </a>
            </li>
            <li className="nav-item">
              <a href="#about" className="nav-link" onClick={closeMenu}>
                <span className="nav-icon">👨‍🎨</span>
                <span className="nav-text"> {t("About", "عني")} </span>
              </a>
            </li>
            <li className="nav-item">
              <a href="#gallery" className="nav-link" onClick={closeMenu}>
                <span className="nav-icon">🖼️</span>
                <span className="nav-text"> {t("Gallery", "المعرض")} </span>
              </a>
            </li>
            <li className="nav-item">
              <a href="#order" className="nav-link" onClick={closeMenu}>
                <span className="nav-icon">✏️</span>
                <span className="nav-text"> {t("Order", "طلب بورتريه")} </span>
              </a>
            </li>
            <li className="nav-item youtube-item">
              <a
                href="https://www.youtube.com/@ShadiArts"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link youtube-link"
                onClick={closeMenu}
              >
                <span className="nav-icon">▶️</span>
                <span className="nav-text"> {t("YouTube", "يوتيوب")} </span>
                <span className="external-indicator">↗</span>
              </a>
            </li>
            <li className="nav-item tiktok-item">
              <a
                href="https://www.tiktok.com/@shadi.arts"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link tiktok-link"
                onClick={closeMenu}
              >
                <span className="nav-icon">🎵</span>
                <span className="nav-text">{t("TikTok", "تيك توك")}</span>
                <span className="external-indicator">↗</span>
              </a>
            </li>
          </ul>

          {/* Social Icons */}
          <div className="navbar-social">
            <a
              href="https://www.youtube.com/@ShadiArts"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon youtube"
              title="YouTube Channel"
            >
              <span>▶️</span>
            </a>
            <a
              href="https://www.tiktok.com/@shadi.arts"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon tiktok"
              title="TikTok"
            >
              <span>🎵</span>
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61591635568693"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon tiktok"
              title="Facebook"
            >
              <span>📘</span>
            </a>
            <a
              href="https://www.instagram.com/shadiarts.art"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon tiktok"
              title="Instagram"
            >
              <span>📸</span>
            </a>
            <a
              href="https://wa.me/201151638804"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon whatsapp"
              title="WhatsApp"
            >
              <span>💬</span>
            </a>
            <a
              href="mailto:shadiarts.official@gmail.com"
              className="social-icon email"
              title="Email"
            >
              <span>📧</span>
            </a>
          </div>

          {/* Dark/Light Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="theme-toggle"
            aria-label="Toggle dark/light mode"
          >
            {theme === "dark" ? "☀️" : "🌙"}
          </button>

          {/* Language Toggle Button */}
          <button
            onClick={toggleLanguage}
            className="language-toggle"
            aria-label="Toggle language"
          >
            {language === "en" ? "🇪🇬 عربي" : "🇬🇧 English"}
          </button>

          {/* Mobile Menu Button */}
          <button
            className={`hamburger ${isMenuOpen ? "active" : ""}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>
      </nav>

      {/* Menu Overlay - closes menu when clicking outside */}
      {isMenuOpen && (
        <div className="menu-overlay active" onClick={closeMenu}></div>
      )}

      {/* YouTube Bar */}
      <div className="youtube-bar">
        <div className="youtube-bar-container">
          <span className="youtube-label">
            🎬 {t("Official Channel", "قناتي الرسمية")}{" "}
          </span>
          <a
            href="https://www.youtube.com/@ShadiArts"
            target="_blank"
            rel="noopener noreferrer"
            className="youtube-channel-link"
          >
            <span className="youtube-icon">▶️</span>
            <span className="channel-name">Shadi Arts</span>
            <span className="subscriber-count">
              {" "}
              {t("Subscribe", "اشترك")}{" "}
            </span>
            <span className="channel-arrow">→</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
